import React from "react";
import {
  Box,
  Image,
  Text,
  SimpleGrid,
  Flex,
  Badge,
  Icon,
  Button,
} from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { FaStar, FaArrowRight, FaCheckCircle, FaLaptopCode } from "react-icons/fa";

export default function NavneetSoftwares() {
  const cards = [
    {
      title: "Supreme Today AI",
      category: "AI Legal Research",
      description: "Ultimate AI-driven legal research tool & single largest collection of Indian law judgments.",
      imgSrc: "./supremeai.jpeg",
      link: "/supremeAi",
      rating: "4.9",
      tag: "Most Popular",
    },
    {
      title: "CDJ Law Journal",
      category: "Digital Law Reporter",
      description: "No. 1 internet-based law reporter in India providing comprehensive high court & supreme court cases.",
      imgSrc: "./cdj-law-journal.jpeg",
      link: "/CDJLawJournal",
      rating: "4.8",
      tag: "Trusted Journal",
    },
    {
      title: "Digilegal",
      category: "Case Law Repository",
      description: "Bombay Cases Reporter and the complete digital legal archive for legal practitioners.",
      imgSrc: "./digilegal.jpeg",
      link: "/digileagal",
      rating: "4.8",
      tag: "Comprehensive",
    },
    {
      title: "Legit Quest",
      category: "Legal Tech & Analytics",
      description: "Next-gen legal analytics, case finder, and citation analysis powered by tech-savvy attorneys.",
      imgSrc: "./legit-quest.jpeg",
      link: "/LegitQuest",
      rating: "4.7",
      tag: "Smart Analytics",
    },
    {
      title: "DLT Publications Online",
      category: "Online Judgments",
      description: "Trusted online judgment portal, providing authoritative citations and high court precedents.",
      imgSrc: "./dlt-publications.jpeg",
      link: "/DltPublications",
      rating: "4.7",
      tag: "Precedents",
    },
    {
      title: "Taxmann Suite",
      category: "Tax & Corporate Law",
      description: "Perfection and accuracy in direct, indirect tax, GST, and corporate legal compliance.",
      imgSrc: "./taxman.jpeg",
      link: "/taxman",
      rating: "4.9",
      tag: "Tax & GST",
    },
  ];

  return (
    <Box py={{ base: "8", md: "12" }} bg="linear-gradient(180deg, #F8FAFC 0%, #EFF6FF 100%)">
      <Box maxW="1360px" mx="auto" px={{ base: "4", md: "6", lg: "8" }}>
        {/* Section Header */}
        <Flex direction="column" align="center" textAlign="center" mb={{ base: "8", md: "10" }}>
          <Badge
            colorScheme="yellow"
            bg="rgba(234, 179, 8, 0.15)"
            color="#A16207"
            px="3.5"
            py="1.5"
            rounded="full"
            fontSize="12px"
            fontWeight="bold"
            letterSpacing="0.8px"
            textTransform="uppercase"
            mb="3"
            display="inline-flex"
            alignItems="center"
            gap="1.5"
          >
            <Icon as={FaLaptopCode} />
            Legal Tech Solutions
          </Badge>

          <Text
            fontSize={{ base: "24px", md: "32px", lg: "36px" }}
            fontWeight="800"
            color="gray.900"
            letterSpacing="-0.5px"
            maxW="850px"
            lineHeight="1.25"
          >
            Empower your business with our cutting-edge software solutions! ✨
          </Text>

          <Text fontSize={{ base: "14px", md: "16px" }} color="gray.600" maxW="640px" mt="2">
            Accelerate your research, streamline case tracking, and explore India's largest legal knowledge bases.
          </Text>
        </Flex>

        {/* Software Cards Grid */}
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={{ base: 4, md: 6, lg: 8 }}>
          {cards.map((card, index) => (
            <Link key={index} to={card.link} style={{ textDecoration: "none" }}>
              <Box
                h="100%"
                bg="#FFFFFF"
                border="1px solid #E2E8F0"
                rounded="2xl"
                overflow="hidden"
                shadow="0 4px 6px -1px rgba(15, 23, 42, 0.05)"
                transition="all 0.3s cubic-bezier(0.16, 1, 0.3, 1)"
                _hover={{
                  transform: "translateY(-6px)",
                  shadow: "0 20px 30px -4px rgba(15, 23, 42, 0.12)",
                  borderColor: "yellow.400",
                }}
                display="flex"
                flexDirection="column"
                role="group"
              >
                {/* Image Header with Badge */}
                <Box
                  position="relative"
                  h={{ base: "180px", md: "200px" }}
                  bg="#F1F5F9"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  p="4"
                  overflow="hidden"
                >
                  <Image
                    src={card.imgSrc}
                    alt={card.title}
                    maxH="100%"
                    maxW="85%"
                    objectFit="contain"
                    transition="transform 0.4s ease"
                    _groupHover={{ transform: "scale(1.06)" }}
                  />

                  <Badge
                    position="absolute"
                    top="3"
                    left="3"
                    bg="#0F172A"
                    color="yellow.400"
                    fontSize="11px"
                    fontWeight="bold"
                    px="2.5"
                    py="0.8"
                    rounded="full"
                    shadow="sm"
                  >
                    {card.tag}
                  </Badge>

                  <Flex
                    position="absolute"
                    top="3"
                    right="3"
                    align="center"
                    gap="1"
                    bg="white"
                    px="2"
                    py="0.5"
                    rounded="full"
                    shadow="sm"
                  >
                    <Icon as={FaStar} color="yellow.400" boxSize="11px" />
                    <Text fontSize="12px" fontWeight="bold" color="gray.800">
                      {card.rating}
                    </Text>
                  </Flex>
                </Box>

                {/* Content Body */}
                <Box p="5" flex="1" display="flex" flexDirection="column" justify="space-between">
                  <Box>
                    <Text fontSize="12px" fontWeight="700" color="#2563EB" textTransform="uppercase" letterSpacing="0.5px" mb="1">
                      {card.category}
                    </Text>

                    <Text fontSize={{ base: "17px", md: "19px" }} fontWeight="700" color="gray.900" lineHeight="1.3">
                      {card.title}
                    </Text>

                    <Text fontSize="13.5px" color="gray.600" mt="2" lineHeight="1.5" noOfLines={3}>
                      {card.description}
                    </Text>
                  </Box>

                  {/* Card CTA */}
                  <Flex
                    align="center"
                    justify="space-between"
                    pt="4"
                    mt="4"
                    borderTop="1px solid #F1F5F9"
                  >
                    <Text
                      fontSize="13px"
                      fontWeight="bold"
                      color="gray.900"
                      _groupHover={{ color: "#2563EB" }}
                      transition="color 0.2s"
                      display="flex"
                      alignItems="center"
                      gap="1.5"
                    >
                      Explore Solution
                      <Icon
                        as={FaArrowRight}
                        boxSize="12px"
                        _groupHover={{ transform: "translateX(4px)" }}
                        transition="transform 0.2s"
                      />
                    </Text>

                    <Badge colorScheme="green" variant="subtle" fontSize="11px" px="2" py="0.5" rounded="md">
                      Instant Access
                    </Badge>
                  </Flex>
                </Box>
              </Box>
            </Link>
          ))}
        </SimpleGrid>
      </Box>
    </Box>
  );
}
